import {
  Banner,
  Button,
  Divider,
  InputSelect,
  InputWrapper,
  type SelectItem,
} from '@vrplatform/components';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import {
  type QueryGroup,
  type QueryKeyUnion,
  type ResponseData,
  captureSentryError,
  useApiIsTeamIdFetching,
  useApiIsTeamSwitching,
  useApiQuery,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import {
  showErrorNotification,
  showWarnNotification,
  useAppName,
} from '@vrplatform/theme';
import { toTitleCase } from '@vrplatform/utils';
import {
  Anchor,
  Avatar,
  Box,
  Center,
  Modal,
  Text,
  UnstyledButton,
  rem,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { Dropzone, type FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import { type ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { getCsvUrl } from '../../utils';
import { SelectConnectionByAppModal } from '../select-connection-by-app-modal';
import { PublicGoogleSheetsParser } from './_google-sheet-parser';
import {
  getCsvImportTeamScopeBlockReason,
  readCsvImportFile,
} from './CsvImportModal.utils';
import { useCsvImportModalv2Mutation } from './useCsvImportModalv2Mutation';

export type CsvImportModalv2App = ResponseData<'get:/apps'>['data'][number];

export type CsvImportModalv2Props = {
  uniqueRef:
    | 'expense'
    | 'payout'
    | 'bankRecords'
    | 'contacts'
    | 'deposits'
    | 'advancedDeposits';
  opened: boolean;
  onSuccess?: Parameters<typeof useCsvImportModalv2Mutation>[0]['onSuccess'];
  closeModal: () => void;
  successMessage: {
    title: string;
    message: string;
  };
  showDateFormatInput: boolean;
  description?: ReactNode;
  submitLabel?: string;
  templateLinkLabel?: string;
  title: string;
  invalidateQueryKeys: (QueryKeyUnion | (string & {}))[];
  invalidateApiQueryGroups?: QueryGroup[];
  accountId?: string;
};

export type CsvImportModalv2SubmitValues = {
  app: CsvImportModalv2App;
  fileData: string;
  fileName: string;
  dateFormat?: string;
};

type FormInputs = {
  appId: string | null;
  file: FileWithPath | null;
  dateFormat: string;
};

type CsvTemplateRow = Record<
  string,
  string | number | boolean | null | undefined
>;

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

const DEFAULT_FORM_VALUES: FormInputs = {
  appId: null,
  file: null,
  dateFormat: DEFAULT_DATE_FORMAT,
};

const EMPTY_AVAILABLE_APPS: CsvImportModalv2App[] = [];

const DATE_FORMAT_OPTIONS: SelectItem[] = [
  {
    label: 'YYYY-MM-DD',
    value: 'YYYY-MM-DD',
  },
  {
    label: 'MM/DD/YYYY',
    value: 'MM/DD/YYYY',
  },
  {
    label: 'DD/MM/YYYY',
    value: 'DD/MM/YYYY',
  },
  {
    label: 'DD.MM.YYYY',
    value: 'DD.MM.YYYY',
  },
];

const CSV_IMPORT_APP_ID = 'csvImport';

export const STORYBOOK_CSV_IMPORT_MODAL_V2_APPS_QUERY_KEY =
  'csv-import-modal-v2-apps';

export const getStorybookCsvImportModalv2AppsQueryKey = (
  uniqueRef: CsvImportModalv2Props['uniqueRef']
) => `${STORYBOOK_CSV_IMPORT_MODAL_V2_APPS_QUERY_KEY}-${uniqueRef}`;

const getCsvFormatLabel = (app: CsvImportModalv2App) =>
  app.id === CSV_IMPORT_APP_ID ? 'VRPlatform' : app.name;

const getCsvFormatErrorMessage = (error: unknown) => {
  if (error && typeof error === 'object') {
    const issues =
      'issues' in error && Array.isArray(error.issues) ? error.issues : [];
    const issueMessage =
      issues[0] &&
      typeof issues[0] === 'object' &&
      issues[0] !== null &&
      'message' in issues[0] &&
      typeof issues[0].message === 'string'
        ? issues[0].message
        : undefined;

    if (issueMessage) return issueMessage;

    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }
  }

  return 'We could not load the available CSV formats. Please try again later.';
};

function useCsvImportModalv2AppsQuery(
  uniqueRef: CsvImportModalv2Props['uniqueRef'],
  opened: boolean
) {
  return useApiQuery<CsvImportModalv2App[], { uniqueRef: string }>(
    async ($api, signal, args) => {
      const result = await $api.GET('/apps', {
        signal,
      });

      if (result.error) throw result.error;

      const filtered = result.data.data.filter((app) =>
        app.importers.some((x) => x.uniqueRef === args.uniqueRef)
      );

      return filtered.sort((a, b) => {
        if (a.id === CSV_IMPORT_APP_ID) return 1;
        if (b.id === CSV_IMPORT_APP_ID) return -1;
        return 0;
      });
    },
    {
      variables: {
        uniqueRef,
      },
      skip: !opened,
      allowDuringTeamSwitch: true,
      queryGroup: 'apps',
      uniqueQueryKey: 'csv-import-modal-v2-apps',
      keepPreviousData: true,
      hideNotification: true,
      errorTitle: 'Failed to load CSV formats',
      storybookQueryKey: getStorybookCsvImportModalv2AppsQueryKey(uniqueRef),
    }
  );
}

const GOOGLE_SHEET_ID_BY_UNIQUE_REF: Record<
  CsvImportModalv2Props['uniqueRef'],
  string | undefined
> = {
  advancedDeposits: '1tXej-Fp3uWDuUUULo8JkWMCTvdfy1yLgMUFAfUT-YJY',
  bankRecords: '1_JjFBkW-NQJfoXnJlISbCuWXja8TXvDPkvO9BdTVvfE',
  contacts: '1-7XcdrHrLM-LQvj1o33MwGGS7U9lk9_Obh3tj9Zh-r0',
  expense: '1MV7r1v1qu_w0W_m8kiyuhHTqGLLOFtU2_Ju7zugLpVQ',
  deposits: '16s24eQopobl4ATtUjT1HeY7Sj0gZScYD2ZstSo9j2Hs', // deposits and payout are same ID
  payout: '16s24eQopobl4ATtUjT1HeY7Sj0gZScYD2ZstSo9j2Hs',
};

export const CsvImportModalv2 = ({
  uniqueRef,
  opened,
  onSuccess,
  closeModal,
  showDateFormatInput,
  description,
  submitLabel,
  templateLinkLabel,
  title,
  successMessage,
  invalidateQueryKeys,
  invalidateApiQueryGroups,
  accountId,
}: CsvImportModalv2Props) => {
  const { appName } = useAppName();
  const theme = useMantineTheme();
  const methods = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const [submitError, setSubmitError] = useState<string | undefined>();
  const isTeamIdFetching = useApiIsTeamIdFetching();
  const isTeamSwitching = useApiIsTeamSwitching();
  const teamScopeBlockReason = getCsvImportTeamScopeBlockReason({
    isTeamIdFetching,
    isTeamSwitching,
  });

  const appsQuery = useCsvImportModalv2AppsQuery(uniqueRef, opened);
  const availableApps = appsQuery.data ?? EMPTY_AVAILABLE_APPS;

  const selectedAppId = methods.watch('appId');
  const selectedDateFormat = methods.watch('dateFormat');
  const selectedFile = methods.watch('file');
  const selectedApp =
    availableApps.find((app) => app.id === selectedAppId) ?? null;

  const canSubmit =
    !!selectedApp &&
    !!selectedFile &&
    (!showDateFormatInput || !!selectedDateFormat) &&
    !appsQuery.isLoading &&
    !teamScopeBlockReason;

  useEffect(() => {
    if (!opened) {
      methods.reset(DEFAULT_FORM_VALUES);
      setSubmitError(undefined);
      return;
    }

    if (availableApps.length > 0) {
      const currentAppId = methods.getValues('appId');

      if (
        !currentAppId ||
        !availableApps.some((app) => app.id === currentAppId)
      ) {
        methods.setValue('appId', availableApps[0].id, {
          shouldDirty: false,
          shouldTouch: false,
          shouldValidate: true,
        });
      }

      return;
    }

    if (methods.getValues('appId')) {
      methods.setValue('appId', null, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: true,
      });
    }
  }, [availableApps, methods, opened]);

  const handleClose = () => {
    methods.reset(DEFAULT_FORM_VALUES);
    setSubmitError(undefined);
    closeModal();
  };

  const {
    closeSelectConnectionModal,
    selectConnectionApp,
    selectConnections,
    submit,
    submitSelectedConnection,
  } = useCsvImportModalv2Mutation({
    uniqueRef,
    successMessage,
    invalidateQueryKeys,
    invalidateApiQueryGroups,
    accountId,
    onSuccess,
    onCompleted: handleClose,
  });

  const GOOGLE_SHEET_ID = GOOGLE_SHEET_ID_BY_UNIQUE_REF[uniqueRef];

  const documentationUrl = selectedApp?.importers.find(
    (importer) => importer.uniqueRef === uniqueRef
  )?.docsLink;

  const handleTemplateDownload = async () => {
    if (!GOOGLE_SHEET_ID) return;

    try {
      const parser = new PublicGoogleSheetsParser(GOOGLE_SHEET_ID, {
        useFormat: true,
      });
      const data = (await parser.parse()) as unknown as CsvTemplateRow[];
      const url = getCsvUrl(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${uniqueRef}_import_template.csv`;
      link.click();
    } catch (error) {
      captureSentryError(error);
      showWarnNotification({
        title: 'Failed to download template',
        message: 'Please contact support if this issue persists.',
      });
    }
  };

  const handleSubmit = methods.handleSubmit(async (values) => {
    if (!selectedApp || !values.file) return;
    if (teamScopeBlockReason) {
      setSubmitError(undefined);
      showWarnNotification({
        title: 'Team access is updating',
        message: teamScopeBlockReason,
      });
      return;
    }

    try {
      setSubmitError(undefined);
      const fileData = await readCsvImportFile(values.file);

      await submit({
        app: selectedApp,
        fileData,
        fileName: values.file.name,
        dateFormat: showDateFormatInput ? values.dateFormat : undefined,
      });
    } catch (error) {
      captureSentryError(error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'We could not import the CSV file. Please try again or contact support if this issue persists.'
      );
    }
  });

  const handleConnectionSubmit = async (connectionId: string) => {
    if (teamScopeBlockReason) {
      setSubmitError(undefined);
      showWarnNotification({
        title: 'Team access is updating',
        message: teamScopeBlockReason,
      });
      return;
    }

    try {
      setSubmitError(undefined);
      await submitSelectedConnection(connectionId);
    } catch (error) {
      captureSentryError(error);
      setSubmitError(
        error instanceof Error
          ? error.message
          : 'We could not import the CSV file. Please try again or contact support if this issue persists.'
      );
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      size={720}
      title={title}
      styles={{
        content: {
          padding: theme.spacing.md,
          paddingTop: 0,
        },
        header: {
          paddingTop: theme.spacing.lg,
        },
        title: {
          width: '100%',
          fontWeight: 500,
        },
      }}
    >
      <Text component="p" c="neutral" mb="lg">
        {description ?? (
          <>
            Upload a CSV file to add {toTitleCase(uniqueRef).toLowerCase()} to{' '}
            {appName}. Your CSV should include header and follow specified
            structure.
          </>
        )}
      </Text>

      <Box component="form" onReset={handleClose} onSubmit={handleSubmit}>
        {appsQuery.isLoading ? (
          <Center aria-label="Loading CSV formats" mih={400} role="status">
            <LoadingIndicator size="sm" />
          </Center>
        ) : (
          <>
            {appsQuery.error ? (
              <Banner
                mounted
                type="error"
                title="We couldn't load CSV formats"
                message={getCsvFormatErrorMessage(appsQuery.error)}
                mb={0}
              />
            ) : availableApps.length === 1 &&
              availableApps[0].id === CSV_IMPORT_APP_ID ? null : (
              <Controller
                control={methods.control}
                name="appId"
                rules={{
                  required: 'Please select a CSV format.',
                }}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    error={fieldState.error?.message}
                    label="CSV Format"
                  >
                    {availableApps.length === 0 ? (
                      <Banner
                        mounted
                        type="warning"
                        title="No CSV formats available"
                        message="No CSV formats are configured for this import yet."
                        mb={0}
                      />
                    ) : availableApps.length === 1 &&
                      availableApps[0].id === CSV_IMPORT_APP_ID ? null : (
                      <Box
                        aria-label="CSV Format"
                        role="group"
                        sx={(currentTheme) => ({
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(104px, 1fr))',
                          gap: currentTheme.spacing.md,
                          marginTop: rem(5),
                        })}
                      >
                        {availableApps.map((app) => (
                          <CsvFormatOptionButton
                            app={app}
                            isSelected={field.value === app.id}
                            key={app.id}
                            onSelect={() => field.onChange(app.id)}
                          />
                        ))}
                      </Box>
                    )}
                  </InputWrapper>
                )}
              />
            )}

            {(GOOGLE_SHEET_ID || documentationUrl) && (
              <Box mt="lg">
                {GOOGLE_SHEET_ID && selectedAppId === CSV_IMPORT_APP_ID && (
                  <Button
                    leftIcon="FileDownloadIcon"
                    onClick={handleTemplateDownload}
                    size="xs"
                    type="button"
                  >
                    {templateLinkLabel ?? 'Download CSV Template'}
                  </Button>
                )}

                {documentationUrl && (
                  <Text c="neutral" mt={GOOGLE_SHEET_ID ? 'xs' : 0} size="sm">
                    Read our{' '}
                    <Anchor
                      href={documentationUrl}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      documentation
                    </Anchor>
                    .
                  </Text>
                )}
              </Box>
            )}

            {showDateFormatInput && (
              <Controller
                control={methods.control}
                name="dateFormat"
                rules={{
                  required: 'Please select a date format.',
                }}
                render={({ field, fieldState }) => (
                  <InputWrapper
                    error={fieldState.error?.message}
                    label="Date Format"
                    mt="lg"
                  >
                    <InputSelect
                      type="single"
                      data={{
                        options: DATE_FORMAT_OPTIONS,
                      }}
                      inputProps={{
                        error: !!fieldState.error,
                      }}
                      setValue={(value) => {
                        if (value?.value) field.onChange(value.value);
                      }}
                      value={
                        DATE_FORMAT_OPTIONS.find(
                          (option) => option.value === field.value
                        ) ?? null
                      }
                    />
                  </InputWrapper>
                )}
              />
            )}

            <Divider mb="md" mt="lg" />

            <Controller
              control={methods.control}
              name="file"
              rules={{
                required: 'Please upload a file.',
                validate: (value) => {
                  if (!value) return 'Please upload a file.';
                  if (value.type !== MIME_TYPES.csv) return 'File must be CSV.';
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <InputWrapper
                  error={fieldState.error?.message}
                  label="Upload CSV"
                >
                  <CsvImportModalv2DropzoneInput
                    error={!!fieldState.error}
                    file={field.value}
                    onChange={(file) => field.onChange(file)}
                    resetFile={() => field.onChange(null)}
                    type={uniqueRef}
                  />
                </InputWrapper>
              )}
            />
          </>
        )}

        <Banner
          mounted={!!teamScopeBlockReason}
          message={
            teamScopeBlockReason ??
            'Team access is still loading. Wait a moment and retry the import.'
          }
          mt="lg"
          size="sm"
          title="Team access is updating"
          type="warning"
        />

        <Banner
          mounted={!!submitError}
          message={
            submitError ??
            'We could not submit the CSV file. Please try again later.'
          }
          mt="lg"
          size="sm"
          title="We couldn't import your file"
          type="error"
        />

        <Box
          mt="lg"
          sx={(currentTheme) => ({
            display: 'flex',
            flexWrap: 'nowrap',
            gap: currentTheme.spacing.sm,
          })}
        >
          <Button disabled={methods.formState.isSubmitting} type="reset">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            loading={methods.formState.isSubmitting}
            sx={{ flex: 1 }}
            type="submit"
            variant="primary"
          >
            {submitLabel ?? `Import ${toTitleCase(uniqueRef).toLowerCase()}`}
          </Button>
        </Box>
      </Box>

      <SelectConnectionByAppModal
        app={selectConnectionApp}
        closeModal={closeSelectConnectionModal}
        connections={selectConnections}
        onSubmit={(connection) => handleConnectionSubmit(connection.id)}
      />
    </Modal>
  );
};

const CsvFormatOptionButton = ({
  app,
  isSelected,
  onSelect,
}: {
  app: CsvImportModalv2App;
  isSelected: boolean;
  onSelect: () => void;
}) => (
  <UnstyledButton
    aria-pressed={isSelected}
    onClick={onSelect}
    sx={(currentTheme) => ({
      borderRadius: currentTheme.radius.lg,
      border: `2px solid ${
        isSelected
          ? currentTheme.colors[currentTheme.primaryColor][6]
          : currentTheme.colors.neutral[2]
      }`,
      backgroundColor: isSelected
        ? currentTheme.colors[currentTheme.primaryColor][0]
        : currentTheme.white,
      padding: currentTheme.spacing.xs,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: rem(108),
      transition: 'border-color 150ms ease, box-shadow 150ms ease',
      boxShadow: isSelected ? currentTheme.shadows.sm : undefined,
    })}
    type="button"
  >
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: rem(6),
        textAlign: 'center',
        width: '100%',
      }}
    >
      {app.icon ? (
        <Avatar
          radius="xl"
          size={44}
          src={app.icon}
          sx={(currentTheme) => ({
            border: `1px solid ${currentTheme.colors.gray[2]}`,
          })}
        />
      ) : (
        <Box
          sx={(currentTheme) => ({
            width: rem(44),
            height: rem(44),
            borderRadius: rem(999),
            backgroundColor:
              app.id === CSV_IMPORT_APP_ID
                ? currentTheme.colors.gray[5]
                : currentTheme.colors[currentTheme.primaryColor][6],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: currentTheme.white,
          })}
        >
          <Icon
            icon={
              app.id === CSV_IMPORT_APP_ID ? 'MoreHorizontalIcon' : 'ShareIcon'
            }
            size={24}
          />
        </Box>
      )}

      <Text fw={500} size="sm">
        {getCsvFormatLabel(app)}
      </Text>
    </Box>
  </UnstyledButton>
);

const CsvImportModalv2DropzoneInput = ({
  error,
  file,
  onChange,
  resetFile,
  type,
}: {
  error: boolean;
  file: FileWithPath | null;
  onChange: (file: FileWithPath) => void;
  resetFile: () => void;
  type: CsvImportModalv2Props['uniqueRef'];
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Dropzone
      accept={[MIME_TYPES.csv]}
      maxSize={3 * 1024 ** 2}
      multiple={false}
      onDrop={(files) => {
        const selectedFile = files[0];

        if (selectedFile) {
          onChange(selectedFile);
        }
      }}
      onFileDialogOpen={resetFile}
      onReject={(files) => {
        const rejectedFile = files[0];
        const message =
          rejectedFile?.errors[0]?.message || 'Please upload a valid CSV file.';

        captureSentryError(message);
        showErrorNotification({
          title: 'Invalid file',
          message,
        });
      }}
      sx={(currentTheme) => {
        const errorBorderColor =
          currentTheme.colors.red[colorScheme === 'dark' ? 4 : 6];
        const defaultBorderColor =
          currentTheme.colors.neutral[colorScheme === 'dark' ? 4 : 2];

        return {
          borderColor: error ? errorBorderColor : defaultBorderColor,
          borderWidth: '2px',
          borderRadius: rem(10),
          borderStyle: 'dashed',
          paddingBlock: currentTheme.spacing.xl,
          marginTop: rem(5),
          '&[data-accept]': {
            borderColor: currentTheme.colors[currentTheme.primaryColor][6],
            backgroundColor:
              currentTheme.colors.neutral[colorScheme === 'dark' ? 3 : 0],
          },
          '&[data-reject]': {
            borderColor: errorBorderColor,
          },
        };
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.spacing.md,
          minHeight: rem(180),
          pointerEvents: 'none',
          textAlign: 'center',
        }}
      >
        <Dropzone.Accept>
          <Icon
            color={
              theme.colors[theme.primaryColor][colorScheme === 'dark' ? 4 : 6]
            }
            icon="CheckCircleIcon"
            size={28}
            strokeWidth={1}
          />
        </Dropzone.Accept>

        <Dropzone.Reject>
          <Icon
            color={theme.colors.red[colorScheme === 'dark' ? 4 : 6]}
            icon="AlertTriangleIcon"
            size={28}
            strokeWidth={1}
          />
        </Dropzone.Reject>

        <Dropzone.Idle>
          <Icon
            icon={file ? 'FileTextIcon' : 'ImportIcon'}
            size={28}
            strokeWidth={1}
          />
        </Dropzone.Idle>

        {file ? (
          <Text
            c={theme.colors[theme.primaryColor][6]}
            fw={500}
            size="md"
            ta="center"
          >
            {file.name}
          </Text>
        ) : (
          <Text c="neutral" ta="center">
            Drag and drop {toTitleCase(type).toLowerCase()} CSV file
            <br />
            or{' '}
            <Text component="span" display="inline" fw={600}>
              select file on disk
            </Text>
          </Text>
        )}
      </Box>
    </Dropzone>
  );
};
